import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Target, Zap, Star, ArrowRight, Home, Users, X, ClipboardCheck } from 'lucide-react';
import { type ModuleData, type ModuleId } from '../../content';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAppStore } from '../../app/hooks/useAppStore';
import { SESSION_CONFIG } from '../../config/constants';
import { useDjaySelection, getDjayGreeting, DjayAvatar } from '../../mascot';
import type { DjayEmotion, DjayVariant } from '../../mascot';
import { APP_NAME } from '../../config/constants';
import { PageHero } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { AssignedAssessmentBanner } from './AssignedAssessmentBanner';
import { PracticeAssignmentBanner } from './PracticeAssignmentBanner';
import { dataLayer } from '../../data/dataLayer';
import type { AssessmentConfig, TeacherAssignment, PracticeAssignment } from '../../assessment';
import { DOMAIN_MODULE_MAP } from '../../assessment';
import { soundManager } from '../../utils/soundManager';
import { getWeeklyActivityForLearner } from '../../utils/activityHelpers';
import { getSmartSessions } from '../../mastery/smartSessions';
import { getTodayChallenge } from '../../utils/dailyChallenge';
import { DailyChallengeCard } from './DailyChallengeCard';
import { GlanceBar } from './GlanceBar';
import { SmartSessionCard } from './SmartSessionCard';
interface HomePageProps {
  modules: ModuleData[];
  state: AppState;
  selectedGroup: GroupId;
  displayName: string;
  onStartPractice: (moduleId: ModuleId) => void;
  onNavigateToModules: () => void;
  onModuleSelect: (module: ModuleData) => void;
  onNavigateToAssessment: () => void;
  onStartAssessment: (config: AssessmentConfig) => void;
  onNavigateToLab?: () => void;
}
export function HomePage({
  modules,
  state,
  selectedGroup,
  displayName,
  onStartPractice,
  onNavigateToModules,
  onModuleSelect,
  onNavigateToAssessment,
  onStartAssessment,
  onNavigateToLab
}: HomePageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    accounts,
    activeAccount
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const [activeAssignments, setActiveAssignments] = useState<TeacherAssignment[]>([]);
  const [activePracticeAssignments, setActivePracticeAssignments] = useState<PracticeAssignment[]>([]);
  const [showKlascodeBanner, setShowKlascodeBanner] = useState(false);
  // Check if learner has no classroom and banner not dismissed
  useEffect(() => {
    let cancelled = false;
    async function checkKlascodeBanner() {
      if (!activeAccount || activeAccount.role !== 'learner') {
        if (!cancelled) setShowKlascodeBanner(false);
        return;
      }
      // Check if banner was dismissed
      const dismissed = await dataLayer.getKlascodeBannerDismissed();
      if (cancelled) return;
      if (dismissed) {
        setShowKlascodeBanner(false);
        return;
      }
      // Check if learner is in any classroom
      const hasClassroom = accounts && Object.values(accounts.classrooms || {}).some(classroom => classroom.learnerIds.includes(activeAccount.id));
      if (!cancelled) setShowKlascodeBanner(!hasClassroom);
    }
    checkKlascodeBanner();
    return () => {
      cancelled = true;
    };
  }, [activeAccount, accounts]);
  const handleDismissKlascodeBanner = async () => {
    await dataLayer.setKlascodeBannerDismissed(true);
    setShowKlascodeBanner(false);
  };
  useEffect(() => {
    let cancelled = false;
    async function loadAssignments() {
      if (!activeAccount) return;
      const [allAssessments, allPractice] = await Promise.all([dataLayer.getTeacherAssignments(), dataLayer.getPracticeAssignments()]);
      if (cancelled) return;
      const now = new Date();
      const validAssessments = Object.values(allAssessments).filter(assignment => {
        if (assignment.group !== selectedGroup) return false;
        if (assignment.deadline && new Date(assignment.deadline) < now) return false;
        const completedBy = assignment.completedByStudentIds || [];
        if (completedBy.includes(activeAccount.id)) return false;
        if (assignment.wholeClass) return true;
        if (assignment.targetStudentIds && assignment.targetStudentIds.includes(activeAccount.id)) return true;
        return false;
      });
      const validPractice = Object.values(allPractice).filter(assignment => {
        if (assignment.deadline && new Date(assignment.deadline) < now) return false;
        const completedBy = assignment.completedByStudentIds || [];
        if (completedBy.includes(activeAccount.id)) return false;
        if (assignment.wholeClass) return true;
        if (assignment.targetStudentIds && assignment.targetStudentIds.includes(activeAccount.id)) return true;
        return false;
      });
      if (!cancelled) {
        setActiveAssignments(validAssessments);
        setActivePracticeAssignments(validPractice);
      }
    }
    loadAssignments();
    return () => {
      cancelled = true;
    };
  }, [activeAccount, selectedGroup]);
  const hour = new Date().getHours();
  let greeting = 'Hallo';
  if (hour < 8) greeting = 'Goedemorgen';else if (hour < 12) greeting = 'Goedemorgen';else if (hour < 18) greeting = 'Goedemiddag';else greeting = 'Goedenavond';
  const djayEmotion: DjayEmotion = useMemo(() => getDjayGreeting(displayName).emotion,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [hour]);
  const currentWeekDays = state.dailyStreak?.currentWeekDays ?? 0;
  const currentStreak = currentWeekDays;
  const todayQuestionsAnswered = state.dailyStreak?.todayQuestionsAnswered ?? 0;
  const dailyGoal = state.dailyStreak?.dailyGoalTarget ?? SESSION_CONFIG.DAILY_GOAL;
  const weeklyGoal = state.dailyStreak?.weeklyGoalTarget ?? 3;
  const level = state.stats.level;
  const xp = state.stats.xp;
  // Dynamic subtitle based on daily progress
  const dynamicSubtitle = useMemo(() => {
    const remaining = dailyGoal - todayQuestionsAnswered;
    if (todayQuestionsAnswered >= dailyGoal) {
      return 'Je dagdoel is gehaald! 🎉';
    } else if (todayQuestionsAnswered > 0) {
      return `Nog ${remaining} ${remaining === 1 ? 'vraag' : 'vragen'} voor je dagdoel!`;
    } else {
      return `Klaar voor een nieuw avontuur in groep ${selectedGroup}? 🚀`;
    }
  }, [todayQuestionsAnswered, dailyGoal, selectedGroup]);
  // F4.1 — three-segment Smart Sessions (replaces recommendation + continue + decay-tip)
  const smartSessions = useMemo(() => {
    return getSmartSessions(state.familyMastery || {}, modules, selectedGroup, getModuleMasteryForGroup, getAnsweredQuestions);
  }, [state.familyMastery, modules, selectedGroup, getModuleMasteryForGroup, getAnsweredQuestions]);
  const hasStarted = modules.some(m => {
    const answered = getAnsweredQuestions(m.id).length;
    return answered > 0;
  });
  // Weekly activity
  const weeklyActivity = useMemo(() => {
    return getWeeklyActivityForLearner(state.questionStates || {});
  }, [state.questionStates]);
  // Daily Challenge
  const dailyChallenge = useMemo(() => {
    return getTodayChallenge(state, modules, selectedGroup);
  }, [state, modules, selectedGroup]);
  const handleStartChallenge = () => {
    if (dailyChallenge.moduleId) {
      onStartPractice(dailyChallenge.moduleId);
    } else {
      onNavigateToModules();
    }
  };
  return <PageContainer data-id="element-1362">
      <GlanceBar streakDays={currentStreak} dailyProgress={todayQuestionsAnswered} dailyGoalTarget={dailyGoal} level={level} xp={xp} weeklyGoal={weeklyGoal} weeklyActivity={weeklyActivity} onNavigateToModules={onNavigateToModules} data-id="element-1363" />

      <motion.div variants={itemVariants} className="mb-6 sm:mb-8" data-id="element-1364">
        <PageHero title={`${greeting}, ${displayName}!`} subtitle={dynamicSubtitle} gradientClasses="from-orange-500 via-amber-500 to-yellow-500" djayEmotion={djayEmotion} djayVariant={selectedVariant} icon={Home} iconBgClass="bg-blue-400" floatingEmojis={['🚀', '⭐']} data-id="element-1365" />
      </motion.div>

      {/* KLASCODE BANNER — hidden when assignments are present to reduce clutter */}
      {showKlascodeBanner && activeAssignments.length === 0 && activePracticeAssignments.length === 0 && <motion.div variants={itemVariants} data-id="element-1366">
            <Card className="p-3 sm:p-4 border-2 border-amber-200 bg-amber-50 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 relative overflow-hidden shadow-sm" data-id="element-1367">
              <div className="flex items-center gap-3" data-id="element-1368">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600 flex-shrink-0 shadow-sm" data-id="element-1369">
                  <Users className="w-5 h-5" data-id="element-1370" />
                </div>
                <div data-id="element-1371">
                  <h3 className="text-sm font-bold text-amber-900" data-id="element-1372">
                    Heb je een klascode?
                  </h3>
                  <p className="text-xs text-amber-700 font-medium" data-id="element-1373">
                    Je kunt deze op elk moment invullen via Instellingen.
                  </p>
                </div>
              </div>
              <button onClick={handleDismissKlascodeBanner} className="absolute top-2 right-2 sm:static p-1.5 text-amber-500 hover:bg-amber-100 rounded-lg transition-colors" title="Sluiten" data-id="element-1374">
                <X className="w-4 h-4" data-id="element-1375" />
              </button>
            </Card>
          </motion.div>}

      {/* ACTIVE ASSIGNMENTS */}
      {activeAssignments.length > 0 && <motion.div variants={itemVariants} data-id="element-1376">
          <Card className="p-4 border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm" data-id="element-1377">
            <div className="flex items-center gap-3" data-id="element-1378">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0 shadow-sm" data-id="element-1379">
                <ClipboardCheck className="w-5 h-5" data-id="element-1380" />
              </div>
              <div data-id="element-1381">
                <h3 className="text-sm font-bold text-indigo-900" data-id="element-1382">
                  {activeAssignments.length}{' '}
                  {activeAssignments.length === 1 ? 'toets staat' : 'toetsen staan'}{' '}
                  voor je klaar!
                </h3>
                <p className="text-xs text-indigo-700 font-medium" data-id="element-1383">
                  Je leraar heeft nieuwe toetsen klaargezet.
                </p>
              </div>
            </div>
            <Button onClick={onNavigateToAssessment} size="sm" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm" data-id="element-1384">
              Bekijk toetsen <ArrowRight className="w-4 h-4 ml-1.5" data-id="element-1385" />
            </Button>
          </Card>
        </motion.div>}

      {/* ACTIVE PRACTICE ASSIGNMENTS */}
      {activePracticeAssignments.map(assignment => <motion.div key={assignment.id} variants={itemVariants} data-id="element-1386">
          <PracticeAssignmentBanner assignment={assignment} teacherName="je leraar" onStartPractice={() => {
        // In a real app, this would set up the practice session with the specific modules/families
        // For now, we just start the first module in the assignment
        if (assignment.moduleIds.length > 0) {
          onStartPractice(assignment.moduleIds[0]);
        } else {
          onNavigateToModules();
        }
      }} data-id="element-1387" />
        </motion.div>)}

      {/* QUICK NAVIGATION */}
      <motion.div variants={itemVariants} className="grid grid-cols-3 gap-3 sm:gap-4" data-id="element-1388">
        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToModules();
      }} className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-blue-200 hover:bg-blue-50 transition-all group shadow-sm" data-id="element-1389">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1390">
            <Target className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-1391" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1392">
            Mijn Werelden
          </span>
        </button>

        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToLab();
      }} className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-purple-200 hover:bg-purple-50 transition-all group shadow-sm" data-id="element-1393">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1394">
            <Zap className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-1395" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1396">
            Oefenlab
          </span>
        </button>

        <button onClick={() => {
        soundManager.playNavigate();
        onNavigateToAssessment();
      }} className="flex flex-col items-center justify-center p-3 sm:p-4 bg-white border-2 border-slate-100 rounded-2xl hover:border-orange-200 hover:bg-orange-50 transition-all group shadow-sm" data-id="element-1397">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform" data-id="element-1398">
            <ClipboardCheck className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-1399" />
          </div>
          <span className="text-xs sm:text-sm font-bold text-slate-700" data-id="element-1400">
            Toets
          </span>
        </button>
      </motion.div>

      {/* PRIMARY ACTION — single SmartSessionCard replaces Continue + Recommendation + Herhaal-tip */}
      <motion.div variants={itemVariants} className="flex flex-col gap-4" data-id="element-1401">
        {hasStarted ? <SmartSessionCard sessions={smartSessions} djayVariant={selectedVariant} onStart={onStartPractice} data-id="element-1402" /> : <WelcomeCard onStart={onNavigateToModules} djayVariant={selectedVariant} data-id="element-1403" />}
      </motion.div>

      {/* DAILY CHALLENGE */}
      {hasStarted && <motion.div variants={itemVariants} data-id="element-1404">
          <DailyChallengeCard challenge={dailyChallenge} djayVariant={selectedVariant} onStart={handleStartChallenge} data-id="element-1405" />
        </motion.div>}
    </PageContainer>;
}
function WelcomeCard({
  onStart,
  djayVariant
}: {
  onStart: () => void;
  djayVariant: DjayVariant;
}) {
  return <Card className="p-6 sm:p-10 text-center bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-100 shadow-lg relative overflow-hidden rounded-[2rem]" data-id="element-1406">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-1407" />
      <div className="relative z-10 flex flex-col items-center" data-id="element-1408">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} className="mb-4 sm:mb-6" data-id="element-1409">
          <div className="block sm:hidden" data-id="element-1410">
            <DjayAvatar size="md" emotion="celebrating" variant={djayVariant} animate data-id="element-1411" />
          </div>
          <div className="hidden sm:block" data-id="element-1412">
            <DjayAvatar size="lg" emotion="celebrating" variant={djayVariant} animate data-id="element-1413" />
          </div>
        </motion.div>
        <h2 className="text-xl sm:text-3xl font-black text-slate-800 mb-2 sm:mb-3 tracking-tight" data-id="element-1414">
          Welkom bij {APP_NAME}!
        </h2>
        <p className="hidden sm:block text-slate-600 text-base sm:text-lg mb-8 max-w-md mx-auto font-medium leading-relaxed" data-id="element-1415">
          Je avontuur begint hier. Kies een wereld om te oefenen, verdien XP en
          bouw je streak op! 🚀
        </p>
        <Button onClick={onStart} variant="primary" size="lg" className="shadow-lg rounded-2xl sm:h-14 sm:text-lg sm:px-8 hover:shadow-xl transition-shadow" data-id="element-1416">
          <Star className="w-5 h-5 sm:w-6 sm:h-6 mr-2 fill-current" data-id="element-1417" />
          Kies je eerste wereld
        </Button>
      </div>
    </Card>;
}